import { useCallback, useState } from "react";
import { getList } from "../../../api/generics";
import Asset from "../../../models/asset";
import DocumentCategory from "../../../models/document-category";
import DocumentSubCategory from "../../../models/document-subcategory";
import Subcompany from "../../../models/subcompany";
import Tag from "../../../models/tag";
import { SUCCESS } from "../../../utils/constants/tags";
import { warningAlert } from "../../utils/messages";

const useSpreadsheetReferenceData = (companyId?: number) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [documentCategories, setDocumentCategories] = useState<
    DocumentCategory[]
  >([]);
  const [documentSubcategories, setDocumentSubcategories] = useState<
    DocumentSubCategory[]
  >([]);
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([]);
  const [tags] = useState<Tag[]>([]);

  const getCompanyParams = () => {
    const additionalParams: Map<string, string> = new Map();
    if (companyId !== undefined) {
      additionalParams.set("company_id", companyId.toString());
    }
    return additionalParams;
  };

  const fetchAssets = useCallback(async () => {
    const additionalParams = getCompanyParams();
    additionalParams.set("include_type", "true");

    const assetsStatus = await getList<Asset>(
      "/assets/",
      1000,
      0,
      additionalParams
    );

    if (assetsStatus.status === SUCCESS) {
      setAssets(assetsStatus.data!.items || []);
    } else {
      warningAlert("Error al cargar los activos.");
      console.error("Error loading assets:", assetsStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchSubcompanies = useCallback(async () => {
    const companiesStatus = await getList<Subcompany>(
      "/subcompanies/",
      1000,
      0,
      getCompanyParams()
    );
    if (companiesStatus.status === SUCCESS) {
      setSubcompanies(companiesStatus.data!.items || []);
    } else {
      warningAlert("Error al cargar las suborganizaciones.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchDocumentCategories = useCallback(async () => {
    const documentcategoriesStatus = await getList<DocumentCategory>(
      "/document_categories/",
      1000,
      0,
      getCompanyParams()
    );
    if (documentcategoriesStatus.status === SUCCESS) {
      setDocumentCategories(documentcategoriesStatus.data!.items || []);
    } else {
      warningAlert("Error al cargar las categorías de documentos.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchDocumentSubcategories = useCallback(async () => {
    const documentSubcategoriesStatus = await getList<DocumentSubCategory>(
      "/document_subcategories/",
      1000,
      0,
      getCompanyParams()
    );
    if (documentSubcategoriesStatus.status === SUCCESS) {
      setDocumentSubcategories(documentSubcategoriesStatus.data!.items || []);
    } else {
      warningAlert("Error al cargar las subcategorías de documentos.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchReferenceData = useCallback(async () => {
    await Promise.all([
      fetchDocumentCategories(),
      fetchDocumentSubcategories(),
      fetchAssets(),
      fetchSubcompanies(),
    ]);
  }, [
    fetchAssets,
    fetchDocumentCategories,
    fetchDocumentSubcategories,
    fetchSubcompanies,
  ]);

  return {
    assets,
    documentCategories,
    documentSubcategories,
    fetchReferenceData,
    subcompanies,
    tags,
  };
};

export default useSpreadsheetReferenceData;
